import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import {
  REPORT_GRAPH,
  REPORT_TABLE,
  SHOW_ALL,
  SHOW_USER,
  SORT_CREATED_NEW_TO_OLD,
  SORT_CREATED_OLD_TO_NEW,
  SORT_NAME_A_Z,
  SORT_NAME_Z_A,
  SORT_UPDATED_NEW_TO_OLD,
  SORT_UPDATED_OLD_TO_NEW,
  STATE_ALL_REPORT_EMBEDS
} from '@opengov/stories-shared/constants';

import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  IPublicationListMessages,
  IPublicationViewMessages,
  IReportEmbedTypes,
  ISortOptions,
  IStoryListMessages,
  IStoryShowOptions
} from '../../../../types';

import { styles } from './ControlBarReport.styles';

export type IControlBarReportProps = {
  publicationMessages?: IPublicationListMessages | IPublicationViewMessages;
  messages: IStoryListMessages;
  show: string;
  sort: string;
  search: string;
  type: IReportEmbedTypes;
  updateSearch(search: string): void;
  updateCreatedBy(show: IStoryShowOptions): void;
  updateReportType(reportType: IReportEmbedTypes): void;
  updateSort(storyVisibility: ISortOptions): void;
};

export const ControlBarReport: React.FC<IControlBarReportProps> = ({
  messages,
  show,
  sort,
  search,
  type,
  updateSearch,
  updateCreatedBy,
  updateReportType,
  updateSort
}) => {
  const [searchValue, setSearchValue] = useState(search);
  const debouncedUpdateSearch = useMemo(() => debounce(updateSearch, 1000), []);
  const prevSearchRef = useRef(search);

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    debouncedUpdateSearch(value);
  };

  useEffect(() => {
    if (search !== prevSearchRef.current) {
      setSearchValue(search);
      prevSearchRef.current = search;
    }
  }, [search]);

  return (
    <Box sx={styles.controls}>
      <Box sx={styles.searchField}>
        <Box component="label" htmlFor="reports-search-input" sx={styles.srOnly}>{messages.find}</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">{messages.find}</Typography>
        <TextField
          size="small"
          placeholder="Find reports by name"
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e.target.value)}
          inputProps={{ id: 'reports-search-input', 'data-test': 'reports-search-input', className: 'no-format' }}
          fullWidth
        />
      </Box>

      <Box sx={{ ...styles.field, ...styles.hide }}>
        <Box component="label" htmlFor="reports-created-by-dropdown" sx={styles.srOnly}>{messages.createdBy}</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">{messages.createdBy}</Typography>
        <Select
          size="small"
          value={show}
          onChange={(e) => updateCreatedBy(e.target.value as IStoryShowOptions)}
          inputProps={{ id: 'reports-created-by-dropdown' }}
          data-test="reports-show-dropdown"
        >
          <MenuItem value={SHOW_ALL}>{messages.anyone}</MenuItem>
          <MenuItem value={SHOW_USER}>{messages.me}</MenuItem>
        </Select>
      </Box>

      <Box sx={styles.field}>
        <Box component="label" htmlFor="reports-report-type-dropdown" sx={styles.srOnly}>Report Type</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">Report Type</Typography>
        <Select
          size="small"
          value={type}
          onChange={(e) => updateReportType(e.target.value as IReportEmbedTypes)}
          inputProps={{ id: 'reports-report-type-dropdown' }}
          data-test="reports-report-type-dropdown"
        >
          <MenuItem value={STATE_ALL_REPORT_EMBEDS}>{messages.all}</MenuItem>
          <MenuItem value={REPORT_GRAPH}>{messages.onlyGraph}</MenuItem>
          <MenuItem value={REPORT_TABLE}>{messages.onlyTable}</MenuItem>
        </Select>
      </Box>

      <Box sx={styles.field}>
        <Box component="label" htmlFor="reports-sort-dropdown" sx={styles.srOnly}>{messages.sort}</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">{messages.sort}</Typography>
        <Select
          size="small"
          value={sort}
          onChange={(e) => updateSort(e.target.value as ISortOptions)}
          inputProps={{ id: 'reports-sort-dropdown' }}
          data-test="reports-sort-dropdown"
        >
          <MenuItem value={SORT_NAME_A_Z}>{messages.nameAZ}</MenuItem>
          <MenuItem value={SORT_NAME_Z_A}>{messages.nameZA}</MenuItem>
          <MenuItem value={SORT_CREATED_NEW_TO_OLD}>{messages.createNewtoOld}</MenuItem>
          <MenuItem value={SORT_CREATED_OLD_TO_NEW}>{messages.createOldtoNew}</MenuItem>
          <MenuItem value={SORT_UPDATED_NEW_TO_OLD}>{messages.updateNewtoOld}</MenuItem>
          <MenuItem value={SORT_UPDATED_OLD_TO_NEW}>{messages.updateOldtoNew}</MenuItem>
        </Select>
      </Box>
    </Box>
  );
};
